/* eslint-disable eqeqeq */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import crossIcon from '../../../assets/close.png';
import { useParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { onSlideChange } from '../../../graphql/subscriptions';
import { submitPollAnswer } from '../../../services/event/pollAnswerService';
import "./attendee-ppt-view.css";
import CpeCredit from '../../cpe-credit/cpe-credit';
import "../../../components/cpe-credit/cpe-credit.css";
import Hls from 'hls.js';

const client = generateClient();

interface Slide {
    id: string;
    sequence: number;
    url: string;
    type: string;
    pollId?: string;
    pollResults?: PollResult[]; // Added pollResults for handling poll results
    collateralTypeId?: string | null;
}

interface PollData {
    pollId: string;
    questionTitle: string;
    options: Record<string, string>;
    pollType?: string; // Added pollType for dynamic handling
}

interface PollResult {
    option: string;
    percentage: number;
    voteCount: number;
}

const PresentationViewer: React.FC<any> = ({ slidesData, pollData, allPollResultData, email, getHeightLiveViewResponsive, customSlidesData }) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [loading] = useState(false);
    const [currentSlide, setCurrentSlide] = useState<Slide | null>();
    const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [otherAnswers, setOtherAnswers] = useState<Record<string, string>>({}); // New state for multiple other answers
    const slideContainerRef = useRef<HTMLDivElement>(null);
    const { eventId } = useParams<{ eventId: string }>();
    const videoRef = useRef<HTMLVideoElement>(null); // Ref for HLS player
    const [isMuted, setIsMuted] = useState(false);
    const isMutedRef = useRef(false); // Ref to track current mute state for HLS events
    const wasFullScreenBeforeSlideChange = useRef(false); // Track if fullscreen should be maintained after slide change

    // Keep the ref in sync with the state
    useEffect(() => {
        isMutedRef.current = isMuted;
    }, [isMuted]);

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            // Check for fullscreen element across all browser prefixes
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement
            );
            setIsFullScreen(isCurrentlyFullscreen);
            // Update tracking flag to reflect current fullscreen state
            wasFullScreenBeforeSlideChange.current = isCurrentlyFullscreen;
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Handle image and video height in .live-short-view when fullscreen state changes
    // This handles cases where fullscreen is changed via ESC key or other browser events
    useEffect(() => {
        const slideContainer = slideContainerRef.current;
        if (!slideContainer) return;

        // Only handle image and video slides (including HLS streams)
        const shouldHandle = currentSlide?.type === 'image' || 
                            currentSlide?.type === 'video' || 
                            currentSlide?.type === 'video/mp4' ||
                            currentSlide?.type === 'HLSStream';
        if (!shouldHandle) return;

        // Find all media elements that might be affected by .live-short-view CSS
        // Check if slideContainer is inside .live-short-view or find .live-short-view in document
        const liveShortView = slideContainer.closest('.live-short-view') || 
                             document.querySelector('.live-short-view');
        
        if (liveShortView) {
            const liveShortViewMedia = liveShortView.querySelectorAll('img, video');
            
            if (isFullScreen) {
                // Entering fullscreen: override CSS !important rule with 100%
                liveShortViewMedia.forEach((mediaEl) => {
                    const element = mediaEl as HTMLElement;
                    if (mediaEl.tagName === 'VIDEO') {
                        // For videos, also set width to ensure full coverage
                        element.style.setProperty('width', '100%', 'important');
                    }
                    element.style.setProperty('height', '100%', 'important');
                });
            } else {
                // Exiting fullscreen: restore CSS height to 250px for .live-short-view
                liveShortViewMedia.forEach((mediaEl) => {
                    const element = mediaEl as HTMLElement;
                    // Maintain width and objectFit for proper image display
                    if (mediaEl.tagName === 'IMG') {
                        element.style.width = '100%';
                        (element as HTMLImageElement).style.objectFit = 'contain';
                    }
                    element.style.setProperty('height', '250px', 'important');
                });
            }
        } else {
            // Handle media elements directly in slideContainer (not in .live-short-view) - big view
            // This applies to images, videos, and HLS streams in the big attendee view
            if (currentSlide?.type === 'image') {
                const img = slideContainer.querySelector('img') as HTMLImageElement;
                if (img) {
                    if (isFullScreen) {
                        img.style.setProperty('width', '100%', 'important');
                        img.style.setProperty('height', '100%', 'important');
                        img.style.objectFit = 'contain';
                    } else {
                        let isMobileDevice = () => window.innerWidth <= 768;  
                        // Maintain width and objectFit for proper image display
                        img.style.width = '100%';
                        img.style.objectFit = 'contain';
                        if(isMobileDevice()){
                            img.style.setProperty('height', '100%', 'important');
                        }else{
                            img.style.setProperty('height', '650px', 'important');
                        }
                    }
                }
            } else if (currentSlide?.type === 'HLSStream') {
                // Handle HLS stream video directly in slideContainer (not in .live-short-view)
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                if (video) {
                    let isMobileDevice = () => window.innerWidth <= 768;
                    if (isFullScreen) {
                        video.style.setProperty('width', '100%', 'important');
                        video.style.setProperty('height', '100%', 'important');
                    } else {
                          if(isMobileDevice()){
                            video.style.setProperty('height', '100%', 'important');
                        }else{
                            video.style.setProperty('height', '650px', 'important');
                        }
                    }
                }
            } else if (currentSlide?.type === 'video' || currentSlide?.type === 'video/mp4') {
                // Handle regular video (MP4) directly in slideContainer (not in .live-short-view)
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                let isMobileDevice = () => window.innerWidth <= 768;
                if (video) {
                    if (isFullScreen) {
                        video.style.setProperty('width', '100%', 'important');
                        video.style.setProperty('height', '100%', 'important');
                    } else {
                        if(isMobileDevice()){
                            video.style.setProperty('height', '100%', 'important');
                        }else{
                            video.style.setProperty('height', '650px', 'important');
                        }
                    }
                }
            }
        }
    }, [isFullScreen, currentSlide]);

    const handleOptionChange = (key: string, type: string) => {
        if (type === 'checkbox') {
            if (selectedAnswers.includes(key)) {
                setSelectedAnswers(selectedAnswers.filter(answer => answer !== key));
                // Clear other answer for this option if it exists
                if (key.toLowerCase() === 'other') {
                    setOtherAnswers(prev => {
                        const newAnswers = { ...prev };
                        delete newAnswers[key];
                        return newAnswers;
                    });
                }
            } else {
                setSelectedAnswers([...selectedAnswers, key]);
            }
        } else {
            setSelectedAnswers([key]);
            // Clear all other answers when switching radio options
            if (key.toLowerCase() !== 'other') {
                setOtherAnswers({});
            }
        }
    };

    useEffect(() => {
        const subscription = client.graphql({ query: onSlideChange });
        const observer = subscription.subscribe({
            next: ({ data }) => {
                // console.log("data---",data);
                const newSlide = data.onSlideChange;
                if (newSlide && newSlide.id === eventId) {
                    // Check if we're currently in fullscreen before slide change
                    wasFullScreenBeforeSlideChange.current = !!document.fullscreenElement;
                    
                    const formattedSlide: Slide = {
                        id: newSlide.id,
                        sequence: newSlide.sequence,
                        url: newSlide.url,
                        type: newSlide.type,
                        pollId: newSlide.pollId,
                        pollResults: newSlide.pollResults?.filter((result: any) => result !== null).map((result: any) => ({
                            option: result.option,
                            percentage: result.percentage,
                            voteCount: result.voteCount
                        })),
                        collateralTypeId: newSlide.collateralTypeId,
                    };
                    setCurrentSlide(formattedSlide);
                    if(window.innerWidth <= 768){
                        setTimeout(()=>{
                            getHeightLiveViewResponsive();
                        }, 100)
                    }
                    setIsSubmitted(false); // Reset submission state on slide change
                }
            },
            error: (error) => console.warn('Subscription error:', error),
        });

        return () => observer.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const defaultSlide = slidesData?.collaterals?.slides.find(
        (slide: Slide) => slide.sequence == 1
      );

      // Track fullscreen state before slide change from slidesData
      wasFullScreenBeforeSlideChange.current = !!document.fullscreenElement;

      if (slidesData?.currentSlide) {
        setCurrentSlide(slidesData?.currentSlide);
      } else {
        setCurrentSlide(defaultSlide);
      }
    }, [eventId, slidesData]);


    useEffect(() => {
        if (currentSlide?.pollId) {
            // Check localStorage for previously submitted answers for the current poll
            const storedAnswers = localStorage.getItem(`poll_${currentSlide.pollId}`);
            if (storedAnswers) {
                setSelectedAnswers(JSON.parse(storedAnswers)); // Parse stored answers as an array
                setIsSubmitted(true); // Hide submit button if answers exist in local storage
            } else {
                // Clear the selected answers if no answers exist in localStorage
                setSelectedAnswers([]);
                setIsSubmitted(false);
            }
        } else {
            // Clear everything if there's no pollId on the slide
            setSelectedAnswers([]);
            setIsSubmitted(false);
        }
    }, [currentSlide]);

    // Function to enter fullscreen mode
    const enterFullScreen = useCallback((slide: Slide | null, pollData?: PollData[], customSlidesData?: any[]) => {
        const slideContainer = slideContainerRef.current;
        if (!slideContainer || !slide) return Promise.resolve(false);

        // For poll questions, check if poll data is available and element is rendered
        if (slide.type === 'pollQuestions' && pollData) {
            const poll = pollData.find((p: PollData) => p.pollId === slide.pollId);
            if (!poll) {
                // Poll data not ready yet
                return Promise.resolve(false);
            }
            // Also check if the poll container is actually rendered in DOM
            const pollContainer = slideContainer.querySelector('.poll-container, .poll-container-fullscreen, .attendee-ppt-view-wrapper');
            if (!pollContainer) {
                // Poll container not rendered yet
                return Promise.resolve(false);
            }
        }

        // For poll results, check if element is rendered
        if (slide.type === 'pollresult') {
            const pollResultContainer = slideContainer.querySelector('.poll-attendee-result, .poll-attendee-result-fullscreen, .attendee-section-wrapper');
            if (!pollResultContainer) {
                return Promise.resolve(false);
            }
        }

        // For custom slides, check if custom slide data is available and element is rendered
        if (slide.type === 'customSlide' && slide.collateralTypeId) {
            if (!customSlidesData || !customSlidesData.find((cs: any) => cs.collateralTypeId === slide.collateralTypeId)) {
                return Promise.resolve(false);
            }
            const customSlideContainer = slideContainer.querySelector('.custom-slide-wrapper-attendee, .custom-slide-wrapper-attendee-fullscreen');
            if (!customSlideContainer) {
                return Promise.resolve(false);
            }
        }

        // For HLS streams, check if video element exists (may need time to initialize)
        if (slide.type === 'HLSStream') {
            const video = slideContainer.querySelector('video') as HTMLVideoElement;
            if (!video) {
                return Promise.resolve(false);
            }
        }

        // For regular videos, check if video element exists
        if (slide.type === 'video' || slide.type === 'video/mp4') {
            const video = slideContainer.querySelector('video') as HTMLVideoElement;
            if (!video) {
                return Promise.resolve(false);
            }
        }

        // ALWAYS use the container element for fullscreen to prevent exit when content changes
        // This way, fullscreen persists even when slides change because the container stays the same
        const elem = slideContainer;

        if (!elem || !elem.requestFullscreen) {
            console.error('No element found for full screen or fullscreen not supported');
            return Promise.resolve(false);
        }

        return elem.requestFullscreen().then(() => {
            // Apply styles to inner elements based on slide type
            if (slide.type === 'image') {
                const img = slideContainer.querySelector('img') as HTMLImageElement;
                if (img) {
                    img.style.width = '100%';
                    // Override CSS !important rule for .live-short-view img
                    img.style.setProperty('height', '100%', 'important');
                    img.style.objectFit = 'contain';
                }
                // Also handle images inside .live-short-view container (if exists)
                const liveShortView = slideContainer.closest('.live-short-view');
                if (liveShortView) {
                    const liveShortViewImgs = liveShortView.querySelectorAll('img, video,');
                    liveShortViewImgs.forEach((imgEl) => {
                        (imgEl as HTMLImageElement).style.setProperty('height', '100%', 'important');
                    });
                }
            }
            
            // Handle HLSStream video height when entering fullscreen
            if (slide.type === 'HLSStream') {
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                if (video) {
                    // Override CSS !important rule for .live-short-view video
                    video.style.setProperty('width', '100%', 'important');
                    video.style.setProperty('height', '100%', 'important');
                    video.controls = false;
                    video.style.pointerEvents = 'none';
                }
                // Also handle HLS videos inside .live-short-view container (if exists)
                const liveShortView = slideContainer.closest('.live-short-view');
                if (liveShortView) {
                    const liveShortViewVideos = liveShortView.querySelectorAll('video');
                    liveShortViewVideos.forEach((videoEl) => {
                        (videoEl as HTMLVideoElement).style.setProperty('width', '100%', 'important');
                        (videoEl as HTMLVideoElement).style.setProperty('height', '100%', 'important');
                    });
                }
            }
            
            // Handle regular video (MP4) height when entering fullscreen
            if (slide.type === 'video' || slide.type === 'video/mp4') {
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                if (video) {
                    // Override CSS !important rule for .live-short-view video
                    video.style.setProperty('width', '100%', 'important');
                    video.style.setProperty('height', '100%', 'important');
                    video.controls = false;
                    video.style.pointerEvents = 'none';
                }
                // Also handle videos inside .live-short-view container (if exists)
                const liveShortView = slideContainer.closest('.live-short-view');
                if (liveShortView) {
                    const liveShortViewVideos = liveShortView.querySelectorAll('video');
                    liveShortViewVideos.forEach((videoEl) => {
                        (videoEl as HTMLVideoElement).style.setProperty('width', '100%', 'important');
                        (videoEl as HTMLVideoElement).style.setProperty('height', '100%', 'important');
                    });
                }
            }
            
            return true;
        }).catch(err => {
            console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            return false;
        });
    }, []);

    // Fullscreen persistence: We use the container element (slideContainer) for fullscreen
    // instead of individual slide elements (img, video). This way, when slides change,
    // fullscreen persists because the container element remains the same in the DOM.
    // The browser only exits fullscreen if the element that is in fullscreen is removed from DOM.

    // Function to exit fullscreen mode
    const exitFullScreen = (slide: Slide | null) => {
        if (!document.exitFullscreen) return;

        const slideContainer = slideContainerRef.current;
        if (!slideContainer) return;

        document.exitFullscreen().then(() => {
            // Reset restoration flag when user manually exits fullscreen
            wasFullScreenBeforeSlideChange.current = false;
            
            // Reset styles on inner elements based on slide type
            if (slide?.type === 'image') {
                const img = slideContainer.querySelector('img') as HTMLImageElement;
                const liveShortView = slideContainer.closest('.live-short-view') || document.querySelector('.live-short-view');
                
                if (img) {
                    // Keep width at 100% and objectFit at 'contain' for proper image display
                    // Only adjust height based on view type
                    img.style.width = '100%';
                    img.style.objectFit = 'contain';
                    // If in .live-short-view, restore to 250px, otherwise restore to 650px for big view
                    const restoreHeight = liveShortView ? '250px' : '650px';
                    img.style.setProperty('height', restoreHeight, 'important');
                }
                // Also handle images inside .live-short-view container (if exists)
                if (liveShortView) {
                    const liveShortViewImgs = liveShortView.querySelectorAll('img, video');
                    liveShortViewImgs.forEach((imgEl) => {
                        const element = imgEl as HTMLImageElement;
                        // Ensure width and objectFit are maintained for proper display
                        if (imgEl.tagName === 'IMG') {
                            element.style.width = '100%';
                            element.style.objectFit = 'contain';
                        }
                        element.style.setProperty('height', '250px', 'important');
                    });
                }
            }
            
            // Restore HLSStream height when exiting fullscreen
            if (slide?.type === 'HLSStream') {
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                const liveShortView = slideContainer.closest('.live-short-view') || document.querySelector('.live-short-view');
                
                if (video) {
                    // If in .live-short-view, restore to 250px, otherwise restore to 650px
                    const restoreHeight = liveShortView ? '250px' : '650px';
                    video.style.setProperty('height', restoreHeight, 'important');
                    video.controls = false;
                    video.style.pointerEvents = 'none';
                }
                // Also handle HLS videos inside .live-short-view container (if exists)
                if (liveShortView) {
                    const liveShortViewVideos = liveShortView.querySelectorAll('video');
                    liveShortViewVideos.forEach((videoEl) => {
                        (videoEl as HTMLVideoElement).style.setProperty('height', '250px', 'important');
                    });
                }
            }
            
            // Restore regular video (MP4) height when exiting fullscreen
            if (slide?.type === 'video' || slide?.type === 'video/mp4') {
                const video = slideContainer.querySelector('video') as HTMLVideoElement;
                const liveShortView = slideContainer.closest('.live-short-view') || document.querySelector('.live-short-view');
                
                if (video) {
                    // If in .live-short-view, restore to 250px, otherwise restore to 650px for big view
                    const restoreHeight = liveShortView ? '250px' : '650px';
                    video.style.setProperty('height', restoreHeight, 'important');
                    video.controls = false;
                    video.style.pointerEvents = 'none';
                }
                // Also handle videos inside .live-short-view container (if exists)
                if (liveShortView) {
                    const liveShortViewVideos = liveShortView.querySelectorAll('video');
                    liveShortViewVideos.forEach((videoEl) => {
                        (videoEl as HTMLVideoElement).style.setProperty('height', '250px', 'important');
                    });
                }
            }
        }).catch(err => {
            console.log(`Error attempting to exit full-screen mode: ${err.message} (${err.name})`);
        });
    };

    const toggleFullScreen = () => {
        // Check for fullscreen element across all browser prefixes
        const isCurrentlyFullscreen = !!(
            document.fullscreenElement ||
            (document as any).webkitFullscreenElement ||
            (document as any).mozFullScreenElement ||
            (document as any).msFullscreenElement
        );
        
        if (!isCurrentlyFullscreen) {
            // Enter fullscreen - using container so it persists across slide changes
            enterFullScreen(currentSlide || null, pollData, customSlidesData);
        } else {
            exitFullScreen(currentSlide || null);
        }
    };

    const handlePollSubmission = async () => {
        if (!currentSlide || !currentSlide.pollId || selectedAnswers.length === 0) return;

        try {
            await submitPollAnswer(
                currentSlide.pollId,
                selectedAnswers,
                email,
                hasOtherOption ? Object.values(otherAnswers).join(', ') : undefined
            );

            setIsSubmitted(true);
            localStorage.setItem(`poll_${currentSlide.pollId}`, JSON.stringify(selectedAnswers));
            // Clear the other answers after submission
            setOtherAnswers({});

        } catch (error) {
            console.error("Error during poll submission:", error);
        }
    };

    const renderPollResults = () => {
        const pollResults = currentSlide?.pollResults || [];
        if (!pollResults.length) {
            return <p>Loading poll results...</p>;
        }

        return (
            <div className={isFullScreen ? 'poll-attendee-result-fullscreen' : 'poll-attendee-result'}>
                <div className="attendee-section-wrapper">
                    <h3>Results</h3>
                    <div className='attendee-result-wrapper-box'>
                    {pollResults
                        .sort((a, b) => b.percentage - a.percentage) // Sorts the array by percentage in descending order
                        .map((result) => (
                            <div className='poll-result-attendee'>
                                <div key={result.option} className="poll-result-item attendee-side-wrapper">
                                    <div className="poll-option attendee-wrapper-option">{result.option}</div>
                                    <div className="poll-percentage">
                                        <div className="poll-bar" style={{ width: `${result.percentage}%`, backgroundColor: '#007bff' }}></div>
                                        <div className="poll-text attendee-side-text">{result.percentage}%</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const displayedSlide = currentSlide || slidesData?.collaterals?.slides.find((slide: Slide) => slide.sequence == 1);
    const poll = pollData.find((poll: PollData) => poll.pollId === currentSlide?.pollId);
    const pollType = poll?.pollType || 'radio'; // Default to 'radio' if pollType is not defined
    const hasOtherOption = selectedAnswers.some((answer) => {
        const optionValue = String(poll?.options[answer] || '').toLowerCase();
        return optionValue === "other" || optionValue === "others";
    });

    const toggleMute = () => {
        if (videoRef.current) {

            const newMutedState = !isMuted;
            videoRef.current.muted = newMutedState;
            setIsMuted(newMutedState);
            isMutedRef.current = newMutedState; // Update the ref as well
        }
    };

    // Initialize HLS.js and handle live stream
    useEffect(() => {
        if (displayedSlide?.type === 'HLSStream' && displayedSlide.url && Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(displayedSlide.url);
            hls.attachMedia(videoRef.current!);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                if (videoRef.current) {
                    videoRef.current.muted = true; // Ensure autoplay works
                    videoRef.current
                        .play()
                        .catch((err) => console.error("Autoplay failed, requiring user interaction", err));
                }
            });

            // Listen for when the video is ready to play, then restore the previous mute state
            hls.on(Hls.Events.LEVEL_LOADED, () => {
                
                if (videoRef.current) {
                    videoRef.current.muted = isMutedRef.current; // Use ref to get current mute state
                    videoRef.current.play().catch(err => console.error("Play failed after level loaded", err));
                }
            });
            
            return () => {
                hls.destroy(); // Cleanup HLS instance
            };
        } else if (displayedSlide?.type === 'video/mp4') {
            if (videoRef.current) {
                videoRef.current.pause(); // Stop playback
                videoRef.current.removeAttribute('src'); // Remove the src attribute
                videoRef.current.load(); // Reset the video
            }
        }
    }, [displayedSlide]);

    const renderCustomSlide = (collateralTypeId: string) => {
        const customSlide = customSlidesData.find((cs: any) => cs.collateralTypeId === collateralTypeId);
        if (!customSlide) return null;
        return (
            <div className={isFullScreen ? "custom-slide-wrapper-attendee-fullscreen" : "custom-slide-wrapper-attendee"}>
                <h2 className="custom-slide-title-attendee">{customSlide.title}</h2>
                <p className="custom-slide-description-attendee">{customSlide.description}</p>
                {/* Render multiple hyperlinks */}
                {customSlide.hyperlinks && customSlide.hyperlinks.length > 0 && (
                    <div className="custom-slide-links-attendee">
                        {customSlide.hyperlinks.map((link: { title: string; url: string }, index: number) => (
                            <div key={index} className="custom-slide-linkw-attendee link-attendee-fullscreen">
                                <a href={link.url} target="_blank" rel="noopener noreferrer" title={link.url}>
                                    {link.title}
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="presentation-viewer" style={{ display: 'flex', flexDirection: 'column', width: '100%', background: '#242424' }}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: "center" }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'red', marginLeft: '10px' }}></span>
                    <h6 style={{ paddingTop: "10px", marginLeft: "10px", color: "white" }}>Live view</h6>
                </div>
                <div>
                    <img src={crossIcon} alt="Close icon" style={{ marginRight: '10px', width: '13px', height: '13px', cursor: 'pointer', display: "none" }} />
                </div>
            </div>
            {loading ? (
                <div className="w-100 h-100 border border-secondary d-flex justify-content-center align-items-center">
                    Loading slides...
                </div>
            ) : (
                <div ref={slideContainerRef} className="position-relative slide-container" style={{ width: '100%', height: '45vh', alignItems: 'center', overflow: 'hidden' }}>
                    <button onClick={toggleFullScreen} className="btn btn-light position-absolute" style={{ bottom: '10px', right: '10px', zIndex: 1  }}>
                        <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} style={{ fontSize: '1.2rem' }} />
                    </button>
                    <div>
                        {displayedSlide?.type === 'HLSStream' ? (
                            <>
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    style={{
                                        width: '100%',
                                        height: isFullScreen ? '100%' : '650px',
                                        objectFit: 'contain',
                                    }}
                                />
                                {/* LIVE Indicator */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '5px',
                                        right: '10px',
                                        padding: '5px 10px',
                                        backgroundColor: 'red',
                                        color: 'white',
                                        fontSize: '12px',
                                        borderRadius: '3px',
                                    }}
                                >
                                    LIVE
                                </div>
                                <div style={{ position: 'absolute', bottom: '10px', left: '15px', display: 'flex', gap: '10px' }}>
                                    <button onClick={toggleMute} className="btn btn-light">
                                        <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
                                    </button>
                                </div>
                            </>
                        ) : displayedSlide?.type === 'video' || displayedSlide?.type === 'video/mp4' ? (
                            <video ref={videoRef} autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}>
                                <source src={displayedSlide.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        ) : displayedSlide?.type === 'pollQuestions' && poll ? (
                            <div className={isFullScreen ? "poll-container-fullscreen attendee-ppt-view-wrapper" : "poll-container attendee-ppt-view-wrapper"}>
                                <h3 className='attendee-ppt-qurstion-wrapper'>Q.{" "+poll.questionTitle}</h3>
                                {isSubmitted ? (
                                    <div className='submit-mesg'>
                                        <p>Thank you for your submission.</p>
                                        {/* {renderPollResults()} */}
                                    </div>
                                ) : (
                                    <div className={isFullScreen ? 'more-potions-fullscreen' : 'attendee-view-poll-wrapper'}>
                                        <form>
                                            {Object.entries(poll.options).map(([key, value]) => (
                                                <div key={key} className='attendee-view-option-wrapper'>
                                                    <input
                                                        type={pollType === 'checkbox' ? 'checkbox' : 'radio'} // Dynamic input type based on pollType
                                                        id={key}
                                                        name="poll"
                                                        value={key}
                                                        checked={pollType === 'checkbox' ? selectedAnswers.includes(key) : selectedAnswers[0] === key}
                                                        onChange={() => handleOptionChange(key, pollType)}
                                                        style={{ marginTop: '5px', // Ensures the radio input aligns with text 
                                                            }}
                                                    />
                                                    <label htmlFor={key} style={{
                                                            cursor: 'pointer',
                                                            lineHeight: '1.5', // Adjust line height for alignment consistency

                                                        }}>{String(value)}</label>
                                            {(String(value).toLowerCase() === "other" || String(value).toLowerCase() === "others") &&
                                                ((pollType === "checkbox" &&
                                                selectedAnswers.includes(key)) ||
                                                (pollType === "radio" &&
                                                    selectedAnswers[0] === key)) && (
                                                <input
                                                    type="text"
                                                    value={otherAnswers[key] || ''}
                                                    onChange={(e) => setOtherAnswers(prev => ({
                                                        ...prev,
                                                        [key]: e.target.value
                                                    }))}
                                                    placeholder="Please specify"
                                                    style={{
                                                    marginLeft: "10px",
                                                    padding: "2px 5px",
                                                    borderRadius: "3px",
                                                    border: "1px solid #ccc",
                                                }}
                                                />
                                                )}
                                            </div>
                                        ))}
                                        </form>
                                        {!isSubmitted && (
                                        <button
                                            onClick={handlePollSubmission}
                                            className="btn btn-primary mt-3 submit"
                                            style={{
                                                width: '120px', 
                                                minWidth: 'fit-content', 
                                                padding: '6px 12px',
                                              }}
                                            disabled={
                                                selectedAnswers.length === 0 ||
                                                (hasOtherOption && 
                                                    selectedAnswers.some(answer => {
                                                        const optionValue = String(poll.options[answer]).toLowerCase();
                                                        return (optionValue === "other" || optionValue === "others") && 
                                                               !otherAnswers[answer]?.trim();
                                                    })
                                                )
                                            }
                                        >
                                            Submit
                                        </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : displayedSlide?.type === 'pollresult' && displayedSlide.pollResults ? (
                            renderPollResults()
                        ) : displayedSlide?.type === 'cpeForm' ? (
                            <CpeCredit className="file3-classname"  />
                         ) : displayedSlide?.type === 'customSlide' && displayedSlide.collateralTypeId ? (
                            <>
                            {renderCustomSlide(displayedSlide.collateralTypeId)}
                            </>
                        ) : (
                            <img crossOrigin='anonymous' src={displayedSlide?.url || ''} alt={`Slide ${displayedSlide?.sequence || 'default'}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresentationViewer;